import {
  AssetRVCriteria,
  AssetUseCriteria,
  FilteringCriteria,
  prFilteringCriteria,
} from 'services/pricing';
import { Col, Form, Input, InputNumber, Radio, Row, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import ButtonLink from 'components/theme_components/ButtonLink';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import OfferInput from 'pages/ProposalV3/PropDetail/OfferInput';
import ParListSelect from 'components/ParListSelect';
import ProductConversionRuleListSelect from 'components/ProductConversionRuleListSelect';
import ProductInput from 'pages/ProposalV3/PropDetail/ProductInput';
import getEnvValue from 'utils/getEnvValue';
import { sumBy } from 'lodash';

"use client"

type ProductTriggerModalProps = {
  setVisible: (visible: boolean) => void
  mode: string
  data: any
  onAddCard?: (newCard: any) => void
  onUpdateCard?: (updatedCard: any) => void
  prcode: string
  existingTriggerLabels?: string[]
}

const ProductTriggerModal = (props: ProductTriggerModalProps) => {
  const { setVisible, mode, data, onAddCard, onUpdateCard, existingTriggerLabels = [] } = props

  const [form] = Form.useForm()
  const [prcode, setprcode] = useState<string>(props.prcode)
  const [triggerEventId, settriggerEventId] = useState<string>()
  const [timeDelayDays, settimeDelayDays] = useState<number>()
  const [triggerConsequence, setTriggerConsequence] = useState<string>()
  const [selectedOption, setSelectedOption] = useState<string>()
  const [selectedEventLabel, setSelectedEventLabel] = useState<string>("")

  const [offersMap, setOffersMap] = useState<Record<string, { label: string; prcode: string }>>({})
  const validateEventUniqueness = (_: any, value: any) => {
    if (value && selectedEventLabel && existingTriggerLabels.includes(selectedEventLabel)) {
      return Promise.reject(new Error("This event already exists"))
    }
    return Promise.resolve()
  }

  useEffect(() => {
    if (mode === "create") {
      form.resetFields()
      settriggerEventId(undefined)
      settimeDelayDays(undefined)
      setTriggerConsequence(undefined)
      setSelectedOption(undefined)
      setprcode(props.prcode)
      setSelectedEventLabel("")
    } else if (data) {
      const consequence = data.isAutomaticConversion ? "Conversion" : data.isCapitalRepayment ? "Remboursement" : ""
      settriggerEventId(data.triggerEventId)
      settimeDelayDays(data.timeDelayDays ?? data.nbDays)
      setTriggerConsequence(consequence)
      setSelectedOption(consequence)
      setSelectedEventLabel(data.triggerEventLabel || "")
      const prcodeValue = data.procdetarget || data.procdeTarget
      if (prcodeValue) {
        setprcode(prcodeValue)
      }
      form.setFieldsValue({
        ...data,
        timeDelayDays: data.timeDelayDays ?? data.nbDays,
        triggerconsequence: consequence,
        procdetarget: prcodeValue,
        triggerEventId: data.triggerEventId || data.eventTrigger,
        triggerEventLabel: data.triggerEventLabel, 
      })
    }
  }, [mode, data, form])

  const handleFieldsChange = () => {
    settriggerEventId(form.getFieldValue("triggerEventId"))
    settimeDelayDays(form.getFieldValue("timeDelayDays"))
    setTriggerConsequence(form.getFieldValue("triggerconsequence"))
  }

  const handleRadioChange = (e: any) => {
    const newValue = e.target.value
    setSelectedOption(newValue)
    setTriggerConsequence(newValue)
    if (newValue !== "Conversion") {
      setprcode(props.prcode)
      form.setFieldsValue({
        procdetarget: undefined,
        offerIdTarget: undefined,
      })
    }
  }

  const handleOffersLoaded = useCallback((offers: any[]) => {
    const newMap: Record<string, { label: string; prcode: string }> = {}
    offers.forEach((offer) => {
      newMap[offer.offerid] = {
        label: offer.offerlabel,
        prcode: offer.prcode,
      }
    })
    setOffersMap(newMap)
  }, [])

  const getFilterList = (_assets?: any[], _offerCriteriaList?: Array<FilteringCriteria>): prFilteringCriteria => {
    const assetList = _assets ?? form.getFieldValue("assets") ?? []
    const assetUseCriteriaList: AssetUseCriteria[] = []
    const assetRVCriteriaList: AssetRVCriteria[] = []
    const offerCriteriaList: FilteringCriteria[] = _offerCriteriaList ?? []

    offerCriteriaList.push({
      critcode: "PROPORIGIN",
      critvalue: form.getFieldValue("ctproporigin") ?? "",
    })

    offerCriteriaList.push({
      critcode: "DEALRATING",
      critvalue: form.getFieldValue("ctdealerrating") ?? "",
    })

    offerCriteriaList.push({
      critcode: "OFFER",
      critvalue: form.getFieldValue("offerid") ?? "0",
    })

    offerCriteriaList.push({
      critcode: "NASSETS",
      critvalue: form.getFieldValue("assets")?.length ?? "0",
    })

    const duration = form
      ?.getFieldValue(["elements", "FIN"])
      ?.find((e: any) => e?.cteid === form.getFieldValue("selectedElement"))?.cteduration

    offerCriteriaList.push({ critcode: "DURATION", critvalue: duration ?? 48 })

    const invest = sumBy(form.getFieldValue("assets"), "assetnetamount")
    offerCriteriaList.push({
      critcode: "INVEST",
      critvalue: String(invest) ?? "0",
    })

    offerCriteriaList.push({
      critcode: "PRCODE",
      critvalue: form.getFieldValue("flagmasterfacility")
        ? form.getFieldValue("mfprcode")
        : (form.getFieldValue("prcode") ?? ""),
    })

    offerCriteriaList.push({
      critcode: "TPTYPE",
      critvalue: form.getFieldValue("client")?.tptype ?? "",
    })

    offerCriteriaList.push({
      critcode: "NETWORK",
      critvalue: form.getFieldValue("ctnetworkcode") ?? "",
    })

    offerCriteriaList.push({
      critcode: "CLIRATING",
      critvalue: form.getFieldValue("client")?.tpsalestatus ?? "",
    })

    assetList?.forEach((a: any) => {
      assetRVCriteriaList.push({
        catalogid: a.catalogid,
        assetid: a.assetid,
        assetsegment: a.assetsegment,
        assetfamily: a.assetfamily,
        assettype: a.assettype,
        assetcategory: a.assetcategory,
        assetmodel: a.assetmodel,
        assetbrand: a.assetbrand,
        assetbrandmodel: a.assetbrandmodel,
        assetnetamount: a.assetnetamount,
        assetfinamount: a.assetfinamount,
        assetvariant: a.assetvariant,
      })

      if (getEnvValue("FLAG_APPLY_ASSET_USE")) {
        a.assetusereqList?.forEach((u: any) => {
          assetUseCriteriaList.push({
            assetfamily: a.assetfamily,
            assettype: a.assettype,
            assetcategory: a.assetcategory,
            catalogid: a.catalogid ?? "",
            attcode: u.attcode ?? "",
            attvalue: u.usevalue ?? "",
          })
        })
      }
    })

    return {
      prcode: form.getFieldValue("procdetarget") ?? "",
      tpid: form.getFieldValue("client")?.tpid ?? 0,
      tpidbroker: form.getFieldValue("broker")?.tpid ?? 0,
      tpidmanufacturer: assetList?.[0]?.tpidmanufacturer ?? 0,
      assetUseCriteria: assetUseCriteriaList,
      assetRVCriteria: assetRVCriteriaList,
      offerCriteria: offerCriteriaList,
      flagApplyAssetUse: getEnvValue("FLAG_APPLY_ASSET_USE"),
      flagApplyAttributesExtendDurationValues: true,
      flagApplyAttributesExtendFirstRentalValues: true,
      flagApplyAttributesExtendRVValues: true,
      flagApplyAttributesExtendRateValues: true,
      flagApplyExtendDurationValues: true,
      flagApplyExtendFirstRentalValues: true,
      flagApplyExtendRVValues: true,
      flagApplyExtendRateValues: true,
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const selectedOffer = offersMap[values.offerIdTarget] || {
        label: "",
        prcode: "",
      }

      const card = {
        key: mode === "update" ? data.key : data.length + 1,
        triggerEventId: values.id ?? values.triggerEventId,
        nbDays: Number(values.timeDelayDays),
        triggerEventLabel: values.triggerEventLabel,
        triggerconsequence: values.triggerconsequence,
        offerid: values.offerid,
        eventTrigger: values.triggerEventId,
        procdetarget: values.procdetarget || prcode,
        procdeTarget: selectedOffer.prcode || values.procdetarget || prcode, 
        offerlabel: selectedOffer.label,
        offerIdTarget: values.offerIdTarget,
        isAutomaticConversion: values.triggerconsequence === "Conversion",
        isCapitalRepayment: values.triggerconsequence === "Remboursement",
        eventtarget:
          values.triggerconsequence === "Remboursement"
            ? "EARLYTERM"
            : values.triggerconsequence === "Conversion"
              ? "CTRESCHEDULE"
              : null,
      }

      if (mode === "create") {
        onAddCard && onAddCard(card)
      } else {
        onUpdateCard && onUpdateCard({ ...card, id: data.id })
      }

      setVisible(false)
    } catch (error) {
      // Handle validation errors
    }
  }

  const currentTriggerConsequence = form.getFieldValue("triggerconsequence") || triggerConsequence

  return (
    <Form form={form} layout="vertical" className="no-border-radius ut-wizard-form" onFieldsChange={handleFieldsChange}>
      <Row gutter={16}>
        <Col span={12}>
          <FormInput
            name="triggerEventId"
            rules={[
              { required: true, message: "Veuillez selectionner un evenement" },
              { validator: validateEventUniqueness }, 
            ]}
          >
            <ParListSelect
              parcode="EVTRIGGER"
              onSelectedObj={(value) => {
                const parlistlabelselected = value?.parlistlabel

                setSelectedEventLabel(parlistlabelselected || "")

                if (parlistlabelselected) {
                  form.setFieldsValue({
                    triggerEventLabel: parlistlabelselected,
                  })

                  form.validateFields(["triggerEventId"])
                }

                settriggerEventId(parlistlabelselected)
              }}
            />
          </FormInput>
          <Form.Item name="triggerEventLabel" hidden>
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <FormInput name="timeDelayDays" rules={[{ required: true, message: "Veuillez saisir un jour" }]}>
            <InputNumber min={1} className="no-border-radius ut-wizard-form color-gray" />
          </FormInput>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <FormInput
            name="triggerconsequence"
            rules={[{ required: true, message: "Veuillez sélectionner une option" }]}
            className="color-gray mb-7"
          >
            <Radio.Group onChange={handleRadioChange} value={selectedOption} className="d-flex row">
              <Radio value="Remboursement">
                <GetLabel label="capitalrepay" />
              </Radio>
              <Radio value="Conversion">
                <GetLabel label="autoConversion" />
              </Radio>
            </Radio.Group>
          </FormInput>
        </Col>
      </Row>

      {currentTriggerConsequence === "Conversion" && (
        <Row gutter={16}>
          <Col span={12}>
            <FormInput
              name="procdetarget"
              rules={[{ required: true, message: "Veuillez entrer la famille" }]}
              className="color-gray mb-7"
            >
              <ProductConversionRuleListSelect
                params={{
                  sourcePrcode: props.prcode,
                  targetPrcode: "",
                  isAuthorized: true,
                  page: undefined,
                  size: undefined,
                  sortBy: "",
                  sortDirection: "",
                }}
                onSelected={(value: any) => {
                  setprcode(value.targetPrcode)
                  form.setFieldsValue({ procdetarget: value.targetPrcode })
                }}
                onChange={(value: any) => {
                  form.setFieldsValue({ offerIdTarget: "" })
                }}
              />
            </FormInput>
          </Col>
          <Col span={12}>
            <FormInput
              name="offerIdTarget"
              rules={[{ required: true, message: "Veuillez entrer le code" }]}
              className="color-gray mb-7"
            >
              <OfferInput
                disabled={!prcode}
                params={{
                  prcode: form.getFieldValue("procdetarget"),
                  filtersList: getFilterList(),
                  filters: "",
                  finamount: form.getFieldValue("ctfinancedamount"),
                  duration: form.getFieldValue("cteduration"),
                  searchMode: "PRCODE",
                }}
                onOffersLoaded={handleOffersLoaded}
              />
            </FormInput>
          </Col>
        </Row>
      )}

      <Row justify="end" className="mt-7">
        <ButtonLink comcode="Annuler" className="ut-btn-bordred" onClick={() => setVisible(false)} />
        <ButtonLink
          type="primary"
          comcode="validate"
          htmlType="submit"
          className="ut-reset-btn"
          onClick={handleSubmit}
          iconPosition="end"
        />
      </Row>
    </Form>
  )
}

export default ProductTriggerModal
